import { InternalEventType } from '../../../domain/enums/internal-event-type';
import { Referrer } from '../../../../interfaces/enums/referrer';
import globalStore from '@/store/global';
import globals from '@/utils/globals';
import { GoogleLoginStatus, GSIClient, GSIDismissedReason, GSIId, GSINotification } from './types';
import { AuthenticationProviderType } from '@/domain/enums/authentication-provider-type';

declare const google: GSIClient;
const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));
const GOOGLE_RETRY_LIMIT = 4;
const GOOGLE_RETRY_DELAY = 200;

export class GoogleClient {
	private googleLoadFailCount: number = 0;
	private loadInitializeSDKPromise: Promise<void> | null = null;

	constructor(private clientId: string) {}

	private awaitedLoad(): Promise<void> {
		if (this.loadInitializeSDKPromise) {
			return this.loadInitializeSDKPromise;
		}

		this.loadInitializeSDKPromise = this.initializeSDK();
		return this.loadInitializeSDKPromise;
	}

	public async initializeSDK(): Promise<void> {
		const gsiScript: HTMLScriptElement = document.createElement('script');
		gsiScript.src = 'https://accounts.google.com/gsi/client';
		gsiScript.async = false;
		document.head.appendChild(gsiScript);

		return new Promise((resolve) => this.gsiScriptResolver(resolve, gsiScript));
	}

	public async showYolo(): Promise<string> {
		await this.awaitedLoad();

		return new Promise((resolve) => {
			this.initializeYolo(resolve);

			if (this.shouldSkipSigninPrompt)return;
			if (!this.googleInstance) return;

			this.googleInstance.prompt((notification) => this.emitYoloStatus(notification));
		});
	}

	private gsiScriptResolver(resolve: (value: void | PromiseLike<void>) => void, gsiScript: HTMLScriptElement): void {
		gsiScript.onload = (): void => {
			if (this.googleLoadFailCount > 0) {
				globalStore.actions.catchAuthError(
					new Error(`Google SDK loaded after ${this.googleLoadFailCount} retries.`),
					AuthenticationProviderType.GOOGLE,
					Referrer.GOOGLE_YOLO
				);
			}

			resolve();
		}

		gsiScript.onerror = (): void => {
			if (this.googleLoadFailCount > GOOGLE_RETRY_LIMIT) {
				globalStore.actions.catchAuthError(
					new Error(`Google SDK failed to load after max retries.`),
					AuthenticationProviderType.GOOGLE,
					Referrer.GOOGLE_YOLO
				);
				return resolve();
			}

			this.googleLoadFailCount++;
			void delay(GOOGLE_RETRY_DELAY * (2 ** this.googleLoadFailCount)).then(() => {
				void this.initializeSDK();
			});

			resolve();
		};
	}

	public async renderGoogleButton(options: { element: Element; template: string }, referrer: Referrer | null): Promise<void> {
		await this.awaitedLoad();

		if (!this.googleInstance) return;
		if (!options.element) return;
		if (!options.element.parentElement) return;

		this.googleInstance.renderButton(options.element as HTMLElement, {
			type: options.template === 'Small' ? 'icon' : 'standard',
			shape: options.template === 'Small' ? 'square' : 'rectangular',
			text: 'signin_with',
			theme: 'filled_blue',
			size: 'large',
			width: this.getSiblingButtonWidth(options.element),
			logo_alignment: 'left',
			locale: 'en',
			click_listener: () => {
				globalStore.mutations.setReferrerGoogleAuth(referrer);
			},
		});
	}

	private get isFedCMSupported(): boolean {
		return (
			!!window.navigator &&
			!!window.navigator.credentials &&
			typeof window.navigator.credentials.get === 'function' &&
			this.isChromiumBased()
		);
	}

	private isChromiumBased(): boolean {
		try {
			const userAgent = navigator.userAgent.toLowerCase();
			const isChromium = userAgent.includes('chrome') || userAgent.includes('chromium');
			const chromeVersion = userAgent.match(/chrome\/(\d+)/);
			const isAbove116 = chromeVersion ? parseInt(chromeVersion[1], 10) >= 116 : false;
			const isIos = /iphone|ipad|ipod/.test(userAgent);

			return isChromium && isAbove116 && !isIos;
		} catch {
			return false;
		}
	}

	private emitYoloStatus(notification: GSINotification): void {
		const status: GoogleLoginStatus = GoogleClient.getGoogleLoginStatus(notification);

		switch (status) {
		case GoogleLoginStatus.SKIPPED:
			globals.eventBus.emit(InternalEventType.YOLO_NOT_DISPLAYED);
			break;
		case GoogleLoginStatus.LEGACY_DISPLAYED:
			globals.eventBus.emit(InternalEventType.YOLO_DISPLAYED);
			break;
		case GoogleLoginStatus.LOGGED_IN:
			globalStore.mutations.setReferrerGoogleAuth(Referrer.GOOGLE_YOLO);
			globals.eventBus.emit(InternalEventType.YOLO_DISMISSED);
			break;
		}
	}

	private getSiblingButtonWidth(element: Element): number {
		const siblingButton: Element | null = element.parentNode?.querySelector('.Provider') ?? null;

		if (!siblingButton) {
			return 400;
		}

		return Math.round(siblingButton.getBoundingClientRect().width);
	}

	private initializeYolo(callback: (credentials) => void, parentId?: string): void {
		if (!this.googleInstance) return;

		this.googleInstance.initialize({
			client_id: this.clientId,
			callback: (response) => callback(response.credential),
			context: 'signin',
			ux_mode: 'popup',
			auto_select: false,
			prompt_parent_id: parentId,
			cancel_on_tap_outside: false,
			itp_support: true,
			use_fedcm_for_prompt: this.isFedCMSupported,
			use_fedcm_for_button: this.isFedCMSupported,
		});

		globals.eventBus.emit(InternalEventType.YOLO_INITIALIZED);
	}

	private get googleInstance(): GSIId | null {
		if (typeof google === 'undefined' || !google.accounts || !google.accounts.id) return null;

		return google.accounts.id;
	}

	private get shouldSkipSigninPrompt(): boolean {
		return !!window['OpenIdFlow'];
	}

	private static getGoogleLoginStatus(notification: GSINotification): GoogleLoginStatus {
		if (notification.isDisplayed()) {
			return GoogleLoginStatus.LEGACY_DISPLAYED;
		}

		if (notification.isSkippedMoment()) {
			return GoogleLoginStatus.SKIPPED;
		}

		if (notification.getDismissedReason() === GSIDismissedReason.CREDENTIAL_RETURNED) {
			return GoogleLoginStatus.LOGGED_IN;
		}

		return GoogleLoginStatus.SKIPPED;
	}
}
